import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
type Block = {
  type: 'p';
  text: string;
} | {
  type: 'list';
  items: string[];
} | {
  type: 'links';
  items: {
    label: string;
    href: string;
  }[];
};
type Faq = {
  question: string;
  answer: Block[];
};
const faqs: Faq[] = [{
  question: '¿Qué certificación necesito para participar?',
  answer: [{
    type: 'p',
    text: 'Se recomienda contar con certificación Open Water Diver o superior. Si tenés dudas sobre tu nivel o experiencia, podemos asesorarte antes de la reserva.'
  }]
}, {
  question: '¿El viaje incluye los pasajes aéreos?',
  answer: [{
    type: 'p',
    text: 'No. Los vuelos no están incluidos. Podemos ayudarte a elegir la mejor opción para viajar desde tu ciudad y coordinar la llegada con el grupo.'
  }]
}, {
  question: '¿Puedo comprar mis vuelos por mi cuenta?',
  answer: [{
    type: 'p',
    text: 'Sí. Podés emitir tus vuelos de forma independiente o solicitar asistencia para coordinar horarios y conexiones.'
  }]
}, {
  question: '¿Cómo funcionan los pagos?',
  answer: [{
    type: 'p',
    text: 'Tu lugar se asegura con una reserva inicial de USD 100.'
  }, {
    type: 'p',
    text: 'Dentro de los siguientes 7 días se realiza un segundo pago de USD 590.'
  }, {
    type: 'p',
    text: 'El saldo restante deberá abonarse hasta 30 días antes del inicio del viaje.'
  }, {
    type: 'p',
    text: 'Una vez confirmada la reserva, nuestro equipo se pondrá en contacto para coordinar cada etapa del proceso y responder cualquier consulta relacionada con la experiencia.'
  }]
}, {
  question: '¿Debo pagar alguna tasa para ingresar a Fernando de Noronha?',
  answer: [{
    type: 'p',
    text: 'Sí. Fernando de Noronha posee dos tasas oficiales obligatorias que deben ser abonadas directamente por cada viajero antes de la llegada a la isla:'
  }, {
    type: 'links',
    items: [{
      label: 'Tasa de Preservación Ambiental (TPA)',
      href: 'https://sin-web.noronha.pe.gov.br/pagar_tpa/'
    }, {
      label: 'Ingreso al Parque Nacional Marino (ICMBio)',
      href: 'https://tickets.parnanoronha.com.br/'
    }]
  }, {
    type: 'p',
    text: 'Estas tasas no están incluidas en el valor del paquete y son administradas por organismos oficiales de Fernando de Noronha.'
  }, {
    type: 'p',
    text: 'Recomendamos realizar ambos pagos antes del viaje para llegar a la isla con toda la documentación lista y evitar demoras al arribar.'
  }, {
    type: 'p',
    text: 'Los valores de estas tasas son establecidos por las autoridades locales y pueden actualizarse antes de la fecha del viaje.'
  }]
}, {
  question: '¿Qué incluye el Pack Explorador?',
  answer: [{
    type: 'p',
    text: 'Incluye:'
  }, {
    type: 'list',
    items: ['7 noches de alojamiento', 'Desayunos diarios', '5 almuerzos incluidos', 'Ilha Tour', 'Paseo en barco al atardecer', 'Traslados aeropuerto / posada / aeropuerto', 'Actividades grupales y acompañamiento durante toda la experiencia']
  }, {
    type: 'p',
    text: 'Ideal para quienes desean descubrir Fernando de Noronha sin participar del programa de buceo.'
  }]
}, {
  question: '¿Qué incluye el Pack Buceador?',
  answer: [{
    type: 'p',
    text: 'Incluye todo lo del Pack Explorador más el programa completo de buceo con 10 inmersiones embarcadas distribuidas en 5 salidas.'
  }]
}, {
  question: '¿Necesito llevar mi propio equipo?',
  answer: [{
    type: 'p',
    text: 'No es obligatorio.'
  }, {
    type: 'p',
    text: 'Podés viajar con tu propio equipo o alquilarlo directamente en el destino.'
  }, {
    type: 'p',
    text: 'El alquiler completo para las 10 inmersiones del programa tiene un valor aproximado de USD 300.'
  }]
}, {
  question: '¿Puedo viajar con alguien que no bucea?',
  answer: [{
    type: 'p',
    text: 'Sí. El viaje fue diseñado para que personas que no bucean también puedan disfrutar Fernando de Noronha y todas sus actividades.'
  }]
}, {
  question: '¿Puedo viajar solo?',
  answer: [{
    type: 'p',
    text: 'Por supuesto. Muchos participantes viajan solos y terminan compartiendo la experiencia con un excelente grupo de viajeros y buceadores.'
  }]
}, {
  question: '¿Cómo se asignan las habitaciones?',
  answer: [{
    type: 'p',
    text: 'Las habitaciones son en base doble, triple y cuádruple.'
  }, {
    type: 'p',
    text: 'La asignación se realiza según el orden de reserva y la composición final del grupo.'
  }]
}, {
  question: '¿Cuántas personas participan del viaje?',
  answer: [{
    type: 'p',
    text: 'El grupo es reducido para garantizar una experiencia más personalizada y una mejor coordinación durante toda la estadía.'
  }]
}, {
  question: '¿Quién acompaña el viaje?',
  answer: [{
    type: 'p',
    text: 'El viaje es coordinado por Mauro Aníbal Mariscotti, instructor PADI con amplia experiencia en Fernando de Noronha, destino donde trabajó durante años y conoce en profundidad tanto dentro como fuera del agua.'
  }]
}, {
  question: '¿Qué gastos no están incluidos?',
  answer: [{
    type: 'p',
    text: 'No están incluidos los vuelos, alquiler de equipos, consumos personales, seguros de viaje ni cualquier servicio no especificado dentro del programa.'
  }]
}, {
  question: '¿Fernando de Noronha es apto para acompañantes?',
  answer: [{
    type: 'p',
    text: 'Sí. Además del buceo, la isla ofrece playas, senderos, miradores, snorkel, paseos en barco y experiencias únicas para quienes desean disfrutar del destino sin bucear.'
  }]
}, {
  question: '¿Por qué viajar a Fernando de Noronha con Buceo y Navegación?',
  answer: [{
    type: 'p',
    text: 'Porque no ofrecemos únicamente un viaje de buceo, sino una experiencia completa en uno de los destinos más exclusivos del mundo.'
  }, {
    type: 'p',
    text: 'Nuestro programa incluye beneficios que habitualmente no forman parte de otros paquetes, como 5 almuerzos, Ilha Tour, paseo en barco al atardecer y acompañamiento permanente durante toda la estadía.'
  }, {
    type: 'p',
    text: 'Además, la coordinación está a cargo de Mauro Aníbal Mariscotti, instructor PADI con años de experiencia laboral en Fernando de Noronha, lo que nos permite brindarte acceso a conocimientos, recomendaciones y detalles que solo alguien que conoce profundamente el destino puede ofrecer.'
  }, {
    type: 'p',
    text: 'La diferencia está en conocer el lugar. La verdadera diferencia está en conocerlo de verdad.'
  }]
}];
function FaqItem({
  faq,
  isOpen,
  onClick
}: {
  faq: Faq;
  isOpen: boolean;
  onClick: () => void;
}) {
  return <div className={`rounded-2xl border bg-white overflow-hidden transition-colors duration-300 ${isOpen ? 'border-noronha-emerald/50 shadow-[0_12px_30px_rgba(2,6,23,0.06)]' : 'border-slate-200 hover:border-noronha-emerald/40'}`} data-id="element-167">
      <button onClick={onClick} aria-expanded={isOpen} className="w-full flex items-center gap-4 p-5 md:p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-noronha-emerald/50 rounded-2xl group" data-id="element-168">
        <span className="flex-1 font-heading text-base md:text-lg font-semibold text-slate-900 tracking-tight group-hover:text-noronha-emerald transition-colors" data-id="element-169">
          {faq.question}
        </span>
        <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition-colors duration-300 ${isOpen ? 'bg-noronha-emerald border-noronha-emerald text-white' : 'border-slate-300 text-noronha-emerald'}`} aria-hidden="true" data-id="element-170">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-171" /> : <Plus className="w-4 h-4" data-id="element-172" />}
        </span>
      </button>

      <AnimatePresence initial={false} data-id="element-173">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-174">
            <div className="px-5 md:px-6 pb-6 flex flex-col gap-3" data-id="element-175">
              {faq.answer.map((block, i) => {
            if (block.type === 'p') {
              return <p key={i} className="text-slate-600 text-sm md:text-base leading-relaxed" data-id="element-176">
                      {block.text}
                    </p>;
            }
            if (block.type === 'links') {
              return <ul key={i} className="flex flex-col gap-2.5" data-id="element-177">
                      {block.items.map((item, j) => <li key={j} className="flex items-start gap-3" data-id="element-178">
                          <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-noronha-emerald" data-id="element-179" />
                          <a href={item.href} target="_blank" rel="noopener noreferrer" className="text-noronha-emerald text-sm md:text-base leading-relaxed font-medium underline decoration-noronha-emerald/30 underline-offset-4 hover:decoration-noronha-emerald transition-colors break-words" data-id="element-180">
                            {item.label}
                          </a>
                        </li>)}
                    </ul>;
            }
            return <ul key={i} className="flex flex-col gap-2" data-id="element-181">
                    {block.items.map((item, j) => <li key={j} className="flex items-start gap-3" data-id="element-182">
                        <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-noronha-emerald" data-id="element-183" />
                        <span className="text-slate-600 text-sm md:text-base leading-relaxed" data-id="element-184">
                          {item}
                        </span>
                      </li>)}
                  </ul>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FaqNoronha() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section id="faq" className="relative bg-white py-24 md:py-32" data-id="element-185">
      <div className="container mx-auto px-6" data-id="element-186">
        <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16" data-id="element-187">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-mono text-xs md:text-sm tracking-[0.22em] uppercase text-noronha-emerald block mb-5" data-id="element-188">
            Preguntas frecuentes
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight text-balance mb-5" data-id="element-189">
            Preguntas frecuentes
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-slate-500 text-lg md:text-xl leading-relaxed text-balance" data-id="element-190">
            Todo lo que necesitás saber antes de reservar tu lugar en Fernando
            de Noronha.
          </motion.p>
        </div>

        <div className="max-w-3xl mx-auto flex flex-col gap-3 md:gap-4" data-id="element-191">
          {faqs.map((faq, idx) => <FaqItem key={idx} faq={faq} isOpen={openIndex === idx} onClick={() => setOpenIndex(openIndex === idx ? null : idx)} data-id="element-192" />)}
        </div>
      </div>
    </section>;
}